import React, { useEffect, useState } from 'react'
import { Modal, Form, Row, Col } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import { Claim, Procedure } from '@dataTypes/claimFeature.types'
import ClaimProcedureAttachment from './ClaimProcedureAttachment'
import './EditClaimProcedureModal.css'
import { isClaimApproved } from '@utils/claimUtils'
import moment from 'moment'

type EditClaimProcedureModalProps = {
  isVisible: boolean
  editMode: boolean
  closeModal: () => void
  procedure: Procedure
  onSave: (updatedProcedure: Procedure) => void
  selectedClaim: Claim
}

const EditClaimProcedureModal: React.FC<EditClaimProcedureModalProps> = ({
  isVisible,
  editMode,
  closeModal,
  procedure,
  onSave,
  selectedClaim,
}) => {
  const [localProcedure, setLocalProcedure] = useState<Procedure>({
    ...procedure,
    procedureAttachments: procedure.procedureAttachments || [],
    procedureNotes: procedure.procedureNotes || [],
  })

  const isApproved = isClaimApproved(selectedClaim)

  useEffect(() => {
    if (procedure) {
      setLocalProcedure({
        ...procedure,
        procedureAttachments: procedure.procedureAttachments || [],
        procedureNotes: procedure.procedureNotes || [],
      })
    }
  }, [procedure])

  const handleSave = (): void => {
    onSave(localProcedure)
    closeModal()
  }

  const attachmentRequirementsElement = (): JSX.Element => {
    const reqs: { item: string; isApplied: boolean }[] = []
    const requirements =
      selectedClaim?.attachmentRequirements?.requirements.filter(
        (requirement: any) =>
          requirement.procedureCode === localProcedure.procedureCode,
      ) || []
    if (requirements.length > 0 && requirements[0].required) {
      Object.keys(requirements[0].required).forEach((key) => {
        if (requirements[0].required[key] === true) {
          switch (key) {
            case 'narrative':
              reqs.push({
                item: 'Narrative',
                isApplied:
                  localProcedure.procedureAttachments.find(
                    (attachment: any) => attachment.attachmentType === '03',
                  ) !== undefined,
              })
              break
            case 'xray':
              reqs.push({
                item: 'X-Rays',
                isApplied:
                  localProcedure.procedureAttachments.find(
                    (attachment: any) => attachment.attachmentType === 'RB',
                  ) !== undefined,
              })
              break
            case 'perioChart':
              reqs.push({
                item: 'Periodontal charts',
                isApplied:
                  localProcedure.procedureAttachments.find(
                    (attachment: any) => attachment.attachmentType === 'P6',
                  ) !== undefined,
              })
              break
            case 'other':
              reqs.push({
                item: 'Other attachments',
                isApplied:
                  localProcedure.procedureAttachments.find(
                    (attachment: any) => attachment.attachmentType === 'OZ',
                  ) !== undefined,
              })
              break
          }
        }
      })
    }
    return reqs.length > 0 ? (
      <div className="attachment-requirements-container d-flex flex-column gap-1 my-2 p-2">
        {reqs.map((req, index) => (
          <div className="d-flex align-items-center gap-2" key={index}>
            <span
              className={`icon ${
                req.isApplied && req.isApplied === true
                  ? 'mgc_save_line attachment-applied-icon'
                  : 'mgc_close_circle_line attachment-not-applied-icon'
              }`}
            ></span>
            <span>{req.item}</span>
          </div>
        ))}
      </div>
    ) : (
      <></>
    )
  }

  return (
    <Modal
      show={isVisible}
      onHide={closeModal}
      backdrop="static"
      keyboard={false}
      size="lg"
      className={`edit-claim-procedure-modal ${isApproved ? 'disabled-claims-modal' : ''}`}
    >
      <Modal.Header className="edit-claim-procedure-header" closeButton>
        <Modal.Title>
          {editMode ? 'Edit claim procedure' : 'View claim procedure'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="edit-claim-procedure-body">
        <Row className="gx-2">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Procedure name</Form.Label>
              <Form.Control
                type="text"
                value={localProcedure.procedureName}
                disabled
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Provider</Form.Label>
              <Form.Control
                type="text"
                value={localProcedure.provider}
                disabled
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="gx-2 mt-2">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Procedure date</Form.Label>
              <Form.Control
                type="text"
                // value={localProcedure.procedureDate}
                value={moment(localProcedure.procedureDate).format(
                  'MM/DD/YYYY',
                )}
                disabled
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Tooth information</Form.Label>
              <Form.Control
                type="text"
                value={localProcedure.siteRegion}
                disabled
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="gx-2 mt-2">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Control
                type="text"
                value={localProcedure.status}
                disabled
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Clinic fee ($)</Form.Label>
              <Form.Control
                type="text"
                value={localProcedure.clinic_fee}
                placeholder="Enter new fee"
                disabled
              />
            </Form.Group>
          </Col>
        </Row>
        {/* Attachments */}
        <ClaimProcedureAttachment
          attachments={localProcedure.procedureAttachments}
          onUpdateAttachments={(updatedAttachments) =>
            setLocalProcedure((prev) => ({
              ...prev,
              procedureAttachments: updatedAttachments,
            }))
          }
          editMode={editMode}
        />

        {attachmentRequirementsElement()}
      </Modal.Body>

      <Modal.Footer>
        <Button
          size="md"
          className="button-secondary me-1"
          onClick={closeModal}
        >
          {editMode ? 'Cancel' : 'Close'}
        </Button>
        {editMode && (
          <Button
            size="md"
            className="button-primary button-save-edit-claim"
            onClick={handleSave}
          >
            Save
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default EditClaimProcedureModal
